import { jStat } from "jstat";
import { v4 as uuid } from "uuid";
export { jStat as stats } from "jstat";
export function incr(n) {
    return n + 1;
}
export const generateAgentID = () => uuid();
export const init = {
    scatter: (count, topology, template) => scatter(count, topology, template),
    stack: (count, template) => stack(count, template),
    grid: (topology, template) => grid(topology, template),
};
function scatter(count, topology, template) {
    const { x_bounds, y_bounds } = topology;
    const width = x_bounds[1] - x_bounds[0];
    const height = y_bounds[1] - y_bounds[0];
    const agents = [...Array(count)].map((_) => {
        const position = [
            Math.floor(Math.random() * width) + x_bounds[0],
            Math.floor(Math.random() * height) + y_bounds[0],
        ];
        if (typeof template === "function") {
            return {
                ...template(),
                position,
            };
        }
        else {
            return {
                ...template,
                position,
            };
        }
    });
    return agents;
}
function stack(count, template) {
    const agents = [...Array(count)].map((_) => {
        if (typeof template === "function") {
            return {
                ...template(),
            };
        }
        else {
            return {
                ...template,
            };
        }
    });
    return agents;
}
export function grid(topology, template) {
    const { x_bounds, y_bounds } = topology;
    const width = x_bounds[1] - x_bounds[0];
    const height = y_bounds[1] - y_bounds[0];
    const count = width * height;
    const agents = [...Array(count)].map((_, ind) => {
        const position = [
            (ind % width) + x_bounds[0],
            Math.floor(ind / width) + y_bounds[0],
        ];
        if (typeof template === "function") {
            return {
                ...template(),
                position,
            };
        }
        else {
            return {
                ...template,
                position,
            };
        }
    });
    return agents;
}
export function createLayout(layout, templates, offset = [0, 0, 0]) {
    const height = layout.length;
    const agents = {};
    layout.forEach((row, pos_y) => {
        row.forEach((type, pos_x) => {
            if (type in templates) {
                if (!(type in agents)) {
                    agents[type] = [];
                }
                const agent_name = (templates[type].agent_name ?? type) + agents[type].length;
                agents[type].push({
                    ...templates[type],
                    agent_name,
                    position: [
                        pos_x + offset[0],
                        height - pos_y + offset[1],
                        0 + offset[2],
                    ],
                });
            }
        });
    });
    const agent_list = [];
    for (const key in agents) {
        agent_list.push(...agents[key]);
    }
    return agent_list;
}
function sfc32(a, b, c, d) {
    return function () {
        a >>>= 0;
        b >>>= 0;
        c >>>= 0;
        d >>>= 0;
        var t = (a + b) | 0;
        a = b ^ (b >>> 9);
        b = (c + (c << 3)) | 0;
        c = (c << 21) | (c >>> 11);
        d = (d + 1) | 0;
        t = (t + d) | 0;
        c = (c + t) | 0;
        return (t >>> 0) / 4294967296;
    };
}
export var rng = {
    _random_fn: Math.random,
};
function xmur3(str) {
    for (var i = 0, h = 1779033703 ^ str.length; i < str.length; i++)
        (h = Math.imul(h ^ str.charCodeAt(i), 3432918353)),
            (h = (h << 13) | (h >>> 19));
    return function () {
        h = Math.imul(h ^ (h >>> 16), 2246822507);
        h = Math.imul(h ^ (h >>> 13), 3266489909);
        return (h ^= h >>> 16) >>> 0;
    };
}
export function setSeed(s) {
    const seed = xmur3(s);
    rng._random_fn = sfc32(seed(), seed(), seed(), seed());
    jStat.setRandom(rng._random_fn);
}
export function random() {
    return rng._random_fn();
}
export function distanceBetween(agentA, agentB, distance = "euclidean") {
    const { abs, pow, max, sqrt } = Math;
    const dFuncs = {
        manhattan: (a_pos, b_pos) => abs(a_pos[0] - b_pos[0]) +
            abs(a_pos[1] - b_pos[1]) +
            abs(a_pos[2] - b_pos[2]),
        euclidean: (a_pos, b_pos) => sqrt(pow(a_pos[0] - b_pos[0], 2) +
            pow(a_pos[1] - b_pos[1], 2) +
            pow(a_pos[2] - b_pos[2], 2)),
        euclidean_sq: (a_pos, b_pos) => pow(a_pos[0] - b_pos[0], 2) +
            pow(a_pos[1] - b_pos[1], 2) +
            pow(a_pos[2] - b_pos[2], 2),
        chebyshev: (a_pos, b_pos) => max(abs(a_pos[0] - b_pos[0]), abs(a_pos[1] - b_pos[1]), abs(a_pos[2] - b_pos[2])),
    };
    const aPos = agentA.position || (agentA.get ? agentA.get("position") : undefined);
    const bPos = agentB.position || (agentB.get ? agentB.get("position") : undefined);
    if (!aPos || !bPos) {
        throw new Error("agents must have position");
    }
    return dFuncs[distance](aPos, bPos);
}
export function normalizeVector(vec) {
    const magnitude = Math.sqrt(vec.reduce((acc, val) => acc + val ** 2, 0));
    return vec.map((v) => v / magnitude);
}
export function randomPosition(topology, z_plane = false) {
    const { floor, random } = Math;
    let z_pos = 0;
    if (z_plane) {
        if (!topology.z_bounds) {
            throw new Error("topology z_bounds needed if z_plane flag set to true");
        }
        z_pos = floor(random() * (topology.z_bounds[1] - topology.z_bounds[0]) +
            topology.z_bounds[0]);
    }
    if (!topology.x_bounds || !topology.y_bounds) {
        throw new Error("topology missing x_bounds or y_bounds");
    }
    return [
        floor(random() * (topology.x_bounds[1] - topology.x_bounds[0]) +
            topology.x_bounds[0]),
        floor(random() * (topology.y_bounds[1] - topology.y_bounds[0]) +
            topology.y_bounds[0]),
        z_pos,
    ];
}
export function neighborsOnPosition(agentA, neighbors) {
    return neighbors.filter((neighbor) => {
        const aPos = agentA.position || (agentA.get ? agentA.get("position") : undefined);
        const nPos = neighbor.position ||
            (neighbor.get ? neighbor.get("position") : undefined);
        if (!aPos || !nPos) {
            throw new Error("agents must have position");
        }
        for (let ind = 0; ind < aPos.length; ind++) {
            if (aPos[ind] !== nPos[ind]) {
                return false;
            }
        }
        return true;
    });
}
export function neighborsInRadius(agentA, neighbors, max_radius = 1, min_radius = 0, z_axis = false) {
    return neighbors.filter((neighbor) => {
        const aPos = agentA.position || (agentA.get ? agentA.get("position") : undefined);
        const nPos = neighbor.position ||
            (neighbor.get ? neighbor.get("position") : undefined);
        if (!aPos || !nPos) {
            throw new Error("agents must have position");
        }
        const notZ = z_axis ? 0 : 1;
        for (let ind = 0; ind < aPos.length - 1 * notZ; ind++) {
            const max = [aPos[ind] + max_radius, aPos[ind] - max_radius];
            const min = [aPos[ind] + min_radius, aPos[ind] - min_radius];
            if (!((nPos[ind] <= max[0] && nPos[ind] >= min[0]) ||
                (nPos[ind] >= max[1] && nPos[ind] <= min[1]))) {
                return false;
            }
        }
        return true;
    });
}
export function neighborsInFront(agentA, neighbors, colinear = false) {
    return neighbors.filter((neighbor) => {
        const aPos = agentA.position || (agentA.get ? agentA.get("position") : undefined);
        const aDir = agentA.direction || (agentA.get ? agentA.get("direction") : undefined);
        const nPos = neighbor.position ||
            (neighbor.get ? neighbor.get("position") : undefined);
        if (!aPos || !nPos) {
            throw new Error("agents must have position");
        }
        if (!aDir) {
            throw new Error("agents must have direction");
        }
        if (colinear) {
            const count = aDir.reduce(function (n, val) {
                return n + val;
            }, 0);
            const dx = nPos[0] - aPos[0];
            const dy = nPos[1] - aPos[1];
            const dz = nPos[2] - aPos[2];
            const xt = aDir[0] !== 0 ? dx / aDir[0] : 0;
            const yt = aDir[1] !== 0 ? dy / aDir[1] : 0;
            const zt = aDir[2] !== 0 ? dz / aDir[2] : 0;
            if (count === 1) {
                return ((xt > 0 && dy === 0 && dz === 0) ||
                    (yt > 0 && dx === 0 && dz === 0) ||
                    (zt > 0 && dx === 0 && dy === 0));
            }
            else if (count === 2) {
                return ((xt === yt && xt > 0 && dz === 0) ||
                    (yt === zt && yt > 0 && dx === 0) ||
                    (xt === zt && xt > 0 && dy === 0));
            }
            else if (count === 3) {
                return xt === yt && yt === zt && xt > 0;
            }
            else {
                return false;
            }
            return true;
        }
        const dx = nPos[0] - aPos[0];
        const dy = nPos[1] - aPos[1];
        const dz = nPos[2] - aPos[2];
        const D = aDir[0] * dx + aDir[1] * dy + aDir[2] * dz;
        if (D <= 0) {
            return false;
        }
        return true;
    });
}
export function neighborsBehind(agentA, neighbors, colinear = false) {
    return neighbors.filter((neighbor) => {
        const aPos = agentA.position || (agentA.get ? agentA.get("position") : undefined);
        const aDir = agentA.direction || (agentA.get ? agentA.get("direction") : undefined);
        const nPos = neighbor.position ||
            (neighbor.get ? neighbor.get("position") : undefined);
        if (!aPos || !nPos) {
            throw new Error("agents must have position");
        }
        if (!aDir) {
            throw new Error("agents must have direction");
        }
        if (colinear) {
            const count = aDir.reduce(function (n, val) {
                return n + val;
            }, 0);
            const dx = nPos[0] - aPos[0];
            const dy = nPos[1] - aPos[1];
            const dz = nPos[2] - aPos[2];
            const xt = aDir[0] !== 0 ? (nPos[0] - aPos[0]) / aDir[0] : 0;
            const yt = aDir[1] !== 0 ? (nPos[1] - aPos[1]) / aDir[1] : 0;
            const zt = aDir[2] !== 0 ? (nPos[2] - aPos[2]) / aDir[2] : 0;
            if (count === 1) {
                return ((xt < 0 && dy === 0 && dz === 0) ||
                    (yt < 0 && dx === 0 && dz === 0) ||
                    (zt < 0 && dx === 0 && dy === 0));
            }
            else if (count === 2) {
                return ((xt === yt && xt < 0 && dz === 0) ||
                    (yt === zt && yt < 0 && dx === 0) ||
                    (xt === zt && xt < 0 && dy === 0));
            }
            else if (count === 3) {
                return xt === yt && yt === zt && xt < 0;
            }
            else {
                return false;
            }
            return true;
        }
        const dx = nPos[0] - aPos[0];
        const dy = nPos[1] - aPos[1];
        const dz = nPos[2] - aPos[2];
        const D = aDir[0] * dx + aDir[1] * dy + aDir[2] * dz;
        if (D >= 0) {
            return false;
        }
        return true;
    });
}
//# sourceMappingURL=stdlib.js.map